import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import moment from 'moment';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {useInflight} from 'src/hooks/redux';
import useScrollLoader from 'src/hooks/useScrollLoader';
import {ButtonDropdown} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import {
  CircularLoader,
  type MenuOption,
} from '@spaced-out/ui-design-system/lib/components';
import {TabList, Tab} from '@spaced-out/ui-design-system/lib/components/Tabs';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';

import {
  Panel,
  PanelBody,
  PanelHeader,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {
  SubTitleLarge,
  BodyLarge,
  BodyMediumBold,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Badge} from '@spaced-out/ui-design-system/lib/components/Badge';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {
  getCrmNotifications,
  markAllNotificationsAsRead,
  unreadNotificationsCount,
} from 'src/action-creators/crm';
import {useDispatch} from 'react-redux';
import {Toggle} from '@spaced-out/ui-design-system/lib/components/Toggle';
import {useHistory} from 'src/rerouter/index';
import Notification from '../notification/notification';

import css from './crm-notifications-panel.css';

// @ts-expect-error - TS7006 - Parameter 'notifications' implicitly has an 'any' type.
const groupNotificationsByDate = (notifications) => {
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'days').startOf('day');
  const sevenDaysAgo = moment().subtract(7, 'days').startOf('day');

  return notifications.reduce(
    // @ts-expect-error - TS7006 - Parameter 'groups' implicitly has an 'any' type. | TS7006 - Parameter 'notification' implicitly has an 'any' type.
    (groups, notification) => {
      const createdDate = moment(notification.createdOn);

      if (createdDate.isSame(today, 'day')) {
        groups.today.push(notification);
      } else if (createdDate.isSame(yesterday, 'day')) {
        groups.yesterday.push(notification);
      } else if (createdDate.isAfter(sevenDaysAgo)) {
        groups.pastSevenDays.push(notification);
      } else {
        groups.earlier.push(notification);
      }

      return groups;
    },
    {
      today: [],
      yesterday: [],
      pastSevenDays: [],
      earlier: [],
    },
  );
};

const NotificationSection = ({
  // @ts-expect-error - TS7031 - Binding element 'title' implicitly has an 'any' type.
  title,
  // @ts-expect-error - TS7031 - Binding element 'notifications' implicitly has an 'any' type.
  notifications,
  // @ts-expect-error - TS7031 - Binding element 'setNotifications' implicitly has an 'any' type.
  setNotifications,
  // @ts-expect-error - TS7031 - Binding element 'setUnreadCount' implicitly has an 'any' type.
  setUnreadCount,
  // @ts-expect-error - TS7031 - Binding element 'setMentionUnreadCount' implicitly has an 'any' type.
  setMentionUnreadCount,
  // @ts-expect-error - TS7031 - Binding element 'handleClose' implicitly has an 'any' type.
  handleClose,
}) => {
  if (!notifications.length) {
    return null;
  }

  // @ts-expect-error TS2552
  const handleNotificationClick = (clickedNotification: NotificationType) => {
    // Update notifications state, ensuring we don't duplicate entries
    // @ts-expect-error - TS7006 - Parameter 'prevNotifications' implicitly has an 'any' type.
    setNotifications((prevNotifications) => {
      // @ts-expect-error - TS7006 - Parameter 'notification' implicitly has an 'any' type.
      const updatedNotifications = prevNotifications.map((notification) =>
        notification.id === clickedNotification.id
          ? {...notification, clicked: true}
          : notification,
      );
      return updatedNotifications;
    });

    // Update unread counts
    // @ts-expect-error - TS7006 - Parameter 'prevCount' implicitly has an 'any' type.
    setUnreadCount((prevCount) => Math.max(0, prevCount - 1));
    if (
      clickedNotification.title === 'Candidate Note Tagged' ||
      clickedNotification.title === 'Job Note Tagged'
    ) {
      // @ts-expect-error - TS7006 - Parameter 'prevCount' implicitly has an 'any' type.
      setMentionUnreadCount((prevCount) => Math.max(0, prevCount - 1));
    }
  };

  return (
    <div className={css.notificationSection}>
      <BodyMediumBold className={css.sectionTitle}>{title}</BodyMediumBold>
      {/* @ts-expect-error - TS7006 - Parameter 'notification' implicitly has an 'any' type. */}
      {notifications.map((notification) => (
        <Notification
          key={notification.id}
          notification={notification}
          onNotificationClick={handleNotificationClick}
          handleClose={handleClose}
        />
      ))}
    </div>
  );
};

const CrmNotificationsPanel = ({
  open,
  handleClose,
}: {
  open: boolean;
  handleClose: () => void;
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;

  const tabs = [
    {
      label: t('All', 'All'),
      tabId: 'all',
    },
    {
      label: `@${t('Mentioned', 'Mentioned')}`,
      tabId: 'Mentioned',
    },
  ];
  const router = useHistory();
  const dispatch = useDispatch();
  const [panelTabSelected, setPanelTabSelected] = React.useState(tabs[0]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [mentionUnreadCount, setMentionUnreadCount] = React.useState(0);
  const isLoading = useInflight(getCrmNotifications(0, 15));
  const [unreadOnly, setUnreadOnly] = React.useState(false);

  const [notifications, setNotifications] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [firstLoad, setFirstLoad] = React.useState(true);
  const [callCount, setCallCount] = React.useState(0);

  // Reset state when panel closes
  React.useEffect(() => {
    if (!open) {
      setNotifications([]);
      setPage(0);
      setHasMore(true);
      setLoading(false);
      setFirstLoad(true);
      setCallCount(0);
      setUnreadCount(0);
      setMentionUnreadCount(0);
      setUnreadOnly(false);
    } else {
      setPanelTabSelected(tabs[0]);
    }
  }, [open]);

  // Add useEffect for initial load
  React.useEffect(() => {
    if (open) {
      loadMoreLogs();
    }
  }, [open]);

  const loadMoreLogs = React.useCallback(() => {
    if (loading || !hasMore || callCount >= 10) {
      return;
    }
    setLoading(true);
    dispatch(getCrmNotifications(page, 15))
      .then((res) => {
        // Deduplicate notifications by ID when adding new ones
        // @ts-expect-error - TS2345 - Argument of type '(prevNotifications: never[]) => any[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
        setNotifications((prevNotifications) => {
          //@ts-expect-error
          const newNotifications = res?.content?.filter(
            (newNotif: any) =>
              !prevNotifications.some(
                // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
                (prevNotif) => prevNotif.id === newNotif.id,
              ),
          );
          return [...prevNotifications, ...newNotifications];
        });
        //@ts-expect-error
        setHasMore(!res.last);
        setPage((prevPage) => prevPage + 1);
        setCallCount((prevCount) => prevCount + 1);
        setLoading(false);
        setFirstLoad(false);

        // Calculate counts for all notifications
        //@ts-expect-error
        const allUnread = res.content.filter(
          // @ts-expect-error - TS7006 - Parameter 'notification' implicitly has an 'any' type.
          (notification) => !notification.clicked,
        ).length;
        //@ts-expect-error
        const mentionUnread = res.content.filter(
          // @ts-expect-error - TS7006 - Parameter 'notification' implicitly has an 'any' type.
          (notification) =>
            !notification.clicked &&
            (notification.title === 'Candidate Note Tagged' ||
              notification.title === 'Job Note Tagged'),
        ).length;

        setUnreadCount((prevCount) => prevCount + allUnread);
        setMentionUnreadCount((prevCount) => prevCount + mentionUnread);
      })
      .catch((err) => {
        console.log('err', err);
      });
  }, [dispatch, loading, page, hasMore, callCount]);

  const [containerRef, loaderRef, handleMovement] = useScrollLoader({
    onLoadMore: loadMoreLogs,
    loading,
    done: !hasMore,
  });

  const filteredNotifications = React.useMemo(() => {
    let filtered = notifications;

    // Filter by tab
    if (panelTabSelected.tabId === 'Mentioned') {
      filtered = filtered.filter(
        (notification) =>
          // @ts-expect-error - TS2339 - Property 'title' does not exist on type 'never'.
          notification.title === 'Candidate Note Tagged' ||
          // @ts-expect-error - TS2339 - Property 'title' does not exist on type 'never'.
          notification.title === 'Job Note Tagged',
      );
    }

    if (unreadOnly) {
      // @ts-expect-error - TS2339 - Property 'clicked' does not exist on type 'never'.
      filtered = filtered.filter((notification) => !notification.clicked);
    }

    return filtered;
  }, [notifications, panelTabSelected.tabId, unreadOnly]);

  const groupedNotifications = groupNotificationsByDate(filteredNotifications);

  const onEllipsisMenuAction = (option: MenuOption) => {
    if (option.label === 'Mark all as read') {
      dispatch(markAllNotificationsAsRead()).then(() => {
        setNotifications(
          // @ts-expect-error - TS2345 - Argument of type 'any[]' is not assignable to parameter of type 'SetStateAction<never[]>'. | TS2698 - Spread types may only be created from object types.
          notifications.map((notify) => ({...notify, clicked: true})),
        );
        dispatch(unreadNotificationsCount()).then((res) => {
          //@ts-expect-error
          setUnreadCount(res);
          setMentionUnreadCount(0);
        });
      });
    } else if (option.label === 'Notification settings') {
      router.push('/trm#/settings/account/subscription');
      handleClose();
    }
  };

  const getTabsWithCount = () => {
    return tabs.map((tab) => {
      let count = 0;
      if (tab.tabId === 'all') {
        count = unreadCount;
      } else if (tab.tabId === 'Mentioned') {
        count = mentionUnreadCount;
      }
      return {
        ...tab,
        label: (
          <div className={css.tabLabel}>
            {tab.label}
            {count > 0 && <Badge fill="blue" text={String(count)} />}
          </div>
        ),
      };
    });
  };

  const tabsWithCount = getTabsWithCount();

  return (
    <Panel
      anchor="right"
      initialFocus={2}
      onClose={handleClose}
      size="medium"
      tapOutsideToClose
      isOpen={open}
      hideBackdrop={false}
    >
      <PanelHeader className={css.panelHeader} size="medium" hideCloseBtn>
        <div className={css.panelHeaderContent}>
          <SubTitleLarge>{t('Notifications', 'Notifications')}</SubTitleLarge>
          <div className={css.headerActions}>
            <Toggle
              labelPosition="left"
              name="unread-notifications"
              onChange={() => setUnreadOnly(!unreadOnly)}
              value={unreadOnly ? 'true' : 'false'}
            >
              {t('Unread_only', 'Unread only')}
            </Toggle>
            <ButtonDropdown
              iconLeftName="ellipsis"
              anchorPosition="bottom-end"
              menu={{
                isFluid: false,
                menuDisabled: false,
                options: [
                  {
                    iconLeft: 'circle-check',
                    key: '1',
                    label: t('Mark_all_as_read', 'Mark all as read'),
                  },
                  {
                    iconLeft: 'gear',
                    key: '2',
                    label: t('Notification_settings', 'Notification settings'),
                  },
                ],
                size: 'medium',
              }}
              onOptionSelect={onEllipsisMenuAction}
              size="small"
              type="ghost"
            />
          </div>
        </div>
      </PanelHeader>
      <div className={css.tabSlot}>
        <TabList
          onSelect={(tab) => {
            // @ts-expect-error - TS2345 - Argument of type '{ tabId: string; label?: string | undefined; }' is not assignable to parameter of type 'SetStateAction<{ label: string; tabId: string; }>'.
            setPanelTabSelected(tab);
          }}
          selectedTab={{
            label: panelTabSelected.label,
            tabId: panelTabSelected.tabId,
          }}
          size="medium"
          menuWidth="100%"
        >
          {tabsWithCount.map(({label, tabId}) => (
            <Tab
              key={tabId}
              classNames={{wrapper: css.tabWrapper}}
              // @ts-expect-error - TS2769 - No overload matches this call.
              label={label}
              tabId={tabId}
            />
          ))}
        </TabList>
      </div>
      {isLoading && firstLoad && <CircularLoader className={css.loader} />}
      <div
        // @ts-expect-error - TS2322 - Type 'ReactRef<HTMLElement>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
        ref={containerRef}
        onScrollCapture={handleMovement}
        className={css.scrollContainer}
      >
        <PanelBody className={css.panelBody}>
          {!isLoading && filteredNotifications.length === 0 && (
            <EmptyState
              title={t('No_Notifications_yet', 'No Notifications yet')}
              description={`${t(
                'All_clear_for_now_Keep_an_eye_out',
                'All clear for now. Keep an eye out',
              )}!`}
              imageVariant="file"
            />
          )}
          {groupedNotifications.today.length > 0 && (
            <NotificationSection
              title={t('Today', 'Today')}
              notifications={groupedNotifications.today}
              setNotifications={setNotifications}
              setUnreadCount={setUnreadCount}
              setMentionUnreadCount={setMentionUnreadCount}
              handleClose={handleClose}
            />
          )}
          {groupedNotifications.yesterday.length > 0 && (
            <NotificationSection
              title={t('Yesterday', 'Yesterday')}
              notifications={groupedNotifications.yesterday}
              setNotifications={setNotifications}
              setUnreadCount={setUnreadCount}
              setMentionUnreadCount={setMentionUnreadCount}
              handleClose={handleClose}
            />
          )}
          {groupedNotifications.pastSevenDays.length > 0 && (
            <NotificationSection
              title={t('Past_7_days', 'Past 7 days')}
              notifications={groupedNotifications.pastSevenDays}
              setNotifications={setNotifications}
              setUnreadCount={setUnreadCount}
              setMentionUnreadCount={setMentionUnreadCount}
              handleClose={handleClose}
            />
          )}
          {groupedNotifications.earlier.length > 0 && (
            <NotificationSection
              title={t('Earlier', 'Earlier')}
              notifications={groupedNotifications.earlier}
              setNotifications={setNotifications}
              setUnreadCount={setUnreadCount}
              setMentionUnreadCount={setMentionUnreadCount}
              handleClose={handleClose}
            />
          )}
          {/* @ts-expect-error - TS2322 - Type 'ReactRef<HTMLElement>' is not assignable to type 'Ref<HTMLDivElement> | undefined'. */}
          <div ref={loaderRef}>
            {loading && !firstLoad && <CircularLoader />}
          </div>
        </PanelBody>
      </div>
    </Panel>
  );
};
export default CrmNotificationsPanel;
